import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Package, UsersThree } from '@phosphor-icons/react';
import { Switch } from './ui/switch';

import { PageSkeleton } from './Skeletons';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// basePath: '/product-upload-access' (President) or '/admin/tenants/<id>/product-upload-access' (Super Admin)
export const ProductAccessPanel = ({ basePath = '/product-upload-access' }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}${basePath}`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || 'Could not load'));
  }, [basePath]);

  const call = async (method, path, body) => {
    setBusy(true);
    setError('');
    try {
      const r = await axios[method](`${API}${basePath}${path}`, body);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) return <div className="text-sm text-rose-700" data-testid="product-access-error">{error}</div>;
  if (!data) return <PageSkeleton />;

  const grantedCount = data.users.filter((u) => u.granted).length;

  return (
    <div className="space-y-4" data-testid="product-access-panel">
      <p className="text-sm text-[#3B5A54]">
        Granted users can <b>add products, upload photos/brochures and bulk-import CSVs</b>. Editing, deleting and bulk price
        updates stay with VP / President. Product cost entered by granted users is ignored — VP / President fills it in.
      </p>

      <div className="flex items-center justify-between rounded-xl border border-[#D5E8E4] bg-[#F4F9F7] px-4 py-3">
        <div className="flex items-center gap-2">
          <UsersThree size={18} className="text-[#00a69c]" />
          <div>
            <div className="text-sm font-semibold text-[#052A23]">Everyone (including future users)</div>
            <div className="text-xs text-[#6D8E87]">Overrides individual switches while on</div>
          </div>
        </div>
        <Switch checked={data.everyone} disabled={busy} onCheckedChange={(v) => call('put', '/everyone', { enabled: v })} data-testid="product-access-everyone" />
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => call('post', '/bulk', { granted: true })} disabled={busy} className="text-xs px-3 py-1.5 rounded-lg bg-[#00a69c] text-white font-semibold disabled:opacity-50" data-testid="product-access-grant-all">
          Grant all current users
        </button>
        <button onClick={() => call('post', '/bulk', { granted: false })} disabled={busy} className="text-xs px-3 py-1.5 rounded-lg border border-rose-300 text-rose-700 font-semibold hover:bg-rose-50 disabled:opacity-50" data-testid="product-access-revoke-all">
          Revoke all
        </button>
        <span className="ml-auto text-xs text-[#6D8E87]" data-testid="product-access-count">{grantedCount} granted individually</span>
      </div>
      {error && <div className="text-xs text-rose-700">{error}</div>}

      <div className="divide-y divide-[#E2ECEA] rounded-xl border border-[#E2ECEA] max-h-[420px] overflow-y-auto">
        {data.users.map((u) => (
          <div key={u.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`product-access-row-${u.id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#052A23] truncate">{u.name}</div>
              <div className="text-xs text-[#6D8E87] truncate">{u.role} · {u.email}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {u.builtin && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#E8F5F3] text-[#05735F]">
                  <Package size={10} /> {u.builtin === 'full' ? 'Built-in' : 'Built-in add'}
                </span>
              )}
              {u.builtin === 'full' ? (
                <span className="text-xs text-[#9BB5AF] w-9 text-center">—</span>
              ) : (
                <Switch
                  checked={u.granted || data.everyone}
                  disabled={busy || data.everyone}
                  onCheckedChange={(v) => call('put', `/users/${u.id}`, { granted: v })}
                  data-testid={`product-access-toggle-${u.id}`}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
